import { PivotModel } from 'api-hooks/common';
import { ChevronDown } from 'baseui/icon';
import { StatefulPopover } from 'baseui/popover';
import { Spinner } from 'baseui/spinner';
import { RefreshSVG } from 'common/assets';
import stylesColors from 'common/styles/styles.colors';
import { themedStyled } from 'common/styles/styletron.theme';
import Separator from 'components/common/separator';
import { Button } from 'components/elements/buttons';
import PivotSetting from 'components/pivot/pivot-setting';
import useTranslation from 'next-translate/useTranslation';
import * as React from 'react';

interface Props {
  exportComponent: React.ReactNode;
  onRefresh: () => void;
  pivotSetting: {
    isEditable?: boolean;
    pivot?: PivotModel;
    setPivot: (pivot: PivotModel) => void;
  };
  isLoading?: boolean;
  isError?: boolean;
}

function Loading() {
  return (
    <>
      <Spinner $color={stylesColors.primaryDefault} $size={24} />
      <Separator gap={16} />
    </>
  );
}

export default function ActionComponent(props: Props) {
  const { t } = useTranslation();
  const { isLoading, pivotSetting, exportComponent, onRefresh, isError } =
    props;
  return (
    <Container>
      <DefaultContent>
        {isLoading ? <Loading /> : isError ? null : exportComponent}
        <Button
          onClick={() => {
            onRefresh();
          }}
          startEnhancer={(color, size) => (
            <RefreshSVG color={color} size={size} />
          )}
          buttonType="secondary"
          style={{
            minWidth: '120px',
            padding: '6px 24px',
          }}
        >
          {t('common:refresh')}
        </Button>
      </DefaultContent>
      <StatefulPopover
        placement="bottomRight"
        overrides={{ Body: { style: { zIndex: 3 } } }}
        children={
          <ActionTrigger>
            {t('common:action')} <ChevronDown />
          </ActionTrigger>
        }
        content={({ close }) => (
          <ActionContent>
            <button
              style={{
                width: 0,
                height: 0,
                outline: 'none',
                border: 'none',
                position: 'absolute',
              }}
              // eslint-disable-next-line jsx-a11y/no-autofocus
              autoFocus
            />
            {isLoading ? <Loading /> : exportComponent}
            <Button
              onClick={() => {
                onRefresh();
              }}
              startEnhancer={(color, size) => (
                <RefreshSVG color={color} size={size} />
              )}
              buttonType="tertiary"
              style={{
                minWidth: '120px',
                padding: '6px 24px',
              }}
            >
              {t('common:refresh')}
            </Button>
          </ActionContent>
        )}
      />
      {pivotSetting.isEditable && (
        <>
          <Separator gap={16} />
          <PivotSetting
            pivot={pivotSetting.pivot}
            onPivotChange={pivotSetting.setPivot}
          />
        </>
      )}
    </Container>
  );
}

const Container = themedStyled('div', ({ $theme }) => ({
  display: 'flex',
  [$theme.mediaQuery.maxSm]: {
    marginTop: '16px',
  },
}));

const DefaultContent = themedStyled('div', ({ $theme }) => ({
  display: 'none',

  [$theme.mediaQuery.sm]: {
    display: 'flex',
  },
}));

const ActionTrigger = themedStyled('div', ({ $theme }) => ({
  cursor: 'pointer',
  padding: '8px 24px',
  borderRadius: '8px !important',
  background: 'white',
  border: `1px solid ${$theme.colors.primaryDefault}`,
  color: $theme.colors.primaryDefault,
  display: 'flex',
  alignItems: 'center',
  ':hover': {
    background: $theme.colors.primaryLightest,
    border: `1px solid ${$theme.colors.primaryDefault}`,
  },
  ':active': {
    background: $theme.colors.primaryLightest,
    border: `1px solid ${$theme.colors.primaryLight}`,
  },
  ...$theme.typography.body3Semibold,
  [$theme.mediaQuery.sm]: {
    display: 'none',
  },
}));

const ActionContent = themedStyled('div', ({ $theme }) => ({
  display: 'flex',
  flex: 1,
  flexDirection: 'column',
  backgroundColor: 'white',
  borderRadius: '8px',
  overflow: 'hidden',
}));
